/**
 * PickTiles — co-located picker pieces for onboarding step 2 (Pick a genie).
 *
 * Resolves the 7 featured genie types from the REAL catalogue
 * (`playbook-templates.ts`) plus a "Something else" custom tile, renders the
 * selectable 2-col grid, and a "Browse all" sheet over the full 37-genie set.
 *
 * Brand law: genie-* tokens + signature gradient only; mascot discs via
 * VoiceMascot; Lucide for inline tile icons only. No "AI" language.
 */
import { Phone, HelpCircle, CalendarDays, TrendingUp, Hand, ClipboardList, MessageCircle, Plus, Check } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { VoiceMascot } from "@/components/voice-genie/VoiceMascot";
import { playbookGoals } from "@/pages/UserHub/Playbooks/data/playbook-templates";
import type { GenieTypeChoice, VoiceMascotKey } from "@/types/voice-vision";
import type { LucideIcon } from "lucide-react";

/** Find a play by id across all catalogue goals; returns its parent category headline too. */
const findPlay = (playId: string) => {
  for (const goal of playbookGoals) {
    const play = goal.plays.find((p) => p.id === playId);
    if (play) return { play, category: goal.headline };
  }
  return null;
};

/** Map a real catalogue play -> a featured tile (mascot + inline icon + category). */
interface FeaturedSpec {
  playId: string;
  mascot: VoiceMascotKey;
  icon: LucideIcon;
}

const FEATURED_SPECS: FeaturedSpec[] = [
  { playId: "reception", mascot: "receptionist", icon: Phone },
  { playId: "support", mascot: "support", icon: HelpCircle },
  { playId: "booking", mascot: "booking", icon: CalendarDays },
  { playId: "sales", mascot: "sales", icon: TrendingUp },
  { playId: "customer-onboarding", mascot: "onboarding-role", icon: Hand },
  { playId: "survey", mascot: "survey", icon: ClipboardList },
  { playId: "feedback", mascot: "feedback", icon: MessageCircle },
];

export interface PickTile extends GenieTypeChoice {
  /** Inline Lucide icon (custom tile has no mascot) */
  Icon: LucideIcon;
  /** True for the "Something else" blank-start tile */
  custom?: boolean;
}

/** The 7 featured tiles, resolved from the real catalogue, + a custom tile. */
export const FEATURED_TILES: PickTile[] = [
  ...FEATURED_SPECS.flatMap((spec) => {
    const found = findPlay(spec.playId);
    if (!found) return [];
    return [
      {
        id: found.play.id,
        title: found.play.name,
        blurb: found.play.tagline,
        mascot: spec.mascot,
        category: found.category.replace(/ Genies.*$/, ""),
        Icon: spec.icon,
      } satisfies PickTile,
    ];
  }),
  {
    id: "custom",
    title: "Something else",
    blurb: "Start blank and describe it yourself.",
    mascot: "receptionist",
    category: "Custom",
    Icon: Plus,
    custom: true,
  },
];

/** A single selectable type tile (mascot disc OR inline icon, name, one-liner, selected ring). */
export const GenieTypeTile = ({
  tile,
  selected,
  onSelect,
}: {
  tile: PickTile;
  selected: boolean;
  onSelect: () => void;
}) => (
  <button
    type="button"
    onClick={onSelect}
    aria-pressed={selected}
    className={`relative text-left flex items-center gap-4 rounded-2xl p-4 transition-all ${
      selected
        ? "border-2 border-genie-primary bg-genie-primary/[0.06] shadow-lg shadow-genie-primary/15"
        : "border border-genie-border bg-white shadow-sm hover:border-genie-primary/40 hover:shadow-md"
    }`}
  >
    {tile.custom ? (
      <span className="flex items-center justify-center w-12 h-12 rounded-full bg-genie-background border border-genie-border flex-shrink-0">
        <tile.Icon className="w-5 h-5 text-genie-text-sub" />
      </span>
    ) : (
      <VoiceMascot mascot={tile.mascot} size={42} rounded discSize={48} className="flex-shrink-0" />
    )}

    <span className="flex-1 min-w-0 flex flex-col gap-0.5">
      <span className="text-[14.5px] font-bold text-genie-text-primary tracking-[-0.2px]">
        {tile.title}
      </span>
      <span className="text-[12.5px] text-genie-text-sub leading-snug">{tile.blurb}</span>
    </span>

    {selected && (
      <span className="flex items-center justify-center w-[22px] h-[22px] rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] flex-shrink-0">
        <Check className="w-3 h-3 text-white" strokeWidth={3} />
      </span>
    )}
  </button>
);

/** The full 37-genie catalogue, grouped by category, in a side sheet. */
export const BrowseAllSheet = ({
  open,
  onOpenChange,
  selectedId,
  onPick,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedId: string | null;
  onPick: (choice: GenieTypeChoice) => void;
}) => (
  <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetContent side="right" className="w-full sm:max-w-xl flex flex-col h-full p-0">
      <SheetHeader className="px-6 pt-6 pb-4 border-b border-genie-border-muted">
        <SheetTitle className="text-xl font-extrabold text-genie-text-primary">
          Browse all genie types
        </SheetTitle>
        <SheetDescription className="text-sm text-genie-text-sub">
          Every genie is a job. Pick the one closest to what you need first. You can change anything later.
        </SheetDescription>
      </SheetHeader>

      <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-7">
        {playbookGoals.map((goal) => (
          <div key={goal.id} className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-genie-primary/10 flex-shrink-0">
                <goal.icon className="w-3.5 h-3.5 text-genie-primary" />
              </span>
              <h3 className="text-sm font-bold text-genie-text-primary">{goal.headline}</h3>
            </div>
            <div className="grid grid-cols-1 gap-2">
              {goal.plays.map((play) => {
                const isSel = selectedId === play.id;
                return (
                  <button
                    key={play.id}
                    type="button"
                    onClick={() =>
                      onPick({
                        id: play.id,
                        title: play.name,
                        blurb: play.tagline,
                        mascot: "receptionist",
                        category: goal.headline.replace(/ Genies.*$/, ""),
                      })
                    }
                    className={`text-left rounded-xl px-3.5 py-3 transition-all ${
                      isSel
                        ? "border-2 border-genie-primary bg-genie-primary/[0.06]"
                        : "border border-genie-border bg-white hover:border-genie-primary/40"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-[13.5px] font-bold text-genie-text-primary">
                        {play.name}
                      </span>
                      {isSel && (
                        <Check className="w-3.5 h-3.5 text-genie-primary" strokeWidth={3} />
                      )}
                    </span>
                    <span className="block text-[12px] text-genie-text-sub leading-snug mt-0.5">
                      {play.tagline}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </SheetContent>
  </Sheet>
);
